import React from 'react';
import { BreadcrumbsProps } from '../../../types/DesignSystem/Breadcrumbs';
import { ChevronRight } from '../Iconography/Icons';
import { FOCUS_RING } from '../focusRing';

/**
 * Breadcrumbs (§ 52) — extracted from the shape the Products screens hand-roll
 * (`ProductDeltaHeader`, `ProductViewHeader`): a named `nav` landmark, an `ol`,
 * link items, chevron separators, a non-interactive current page.
 *
 * The decisions that are contracts:
 *
 * - **The landmark is named** (`aria-label`, default "Breadcrumb"), because a
 *   page carries more than one `nav` and an unnamed one is just "navigation".
 * - **The last item is the current page** — a `span[aria-current="page"]` in
 *   full ink. A page does not link to itself, and a self-link is where keyboard
 *   users lose a tab stop to a no-op.
 * - **`href` renders an `<a>`, `onClick` alone renders a `<button>`, NEITHER
 *   renders a `<span>` (§ 69).** Anchors keep middle-click and copy-link working;
 *   router callers pass both and `preventDefault()` in the handler. The span is
 *   for a waypoint with no page of its own (a grouping level) — never a focusable
 *   no-op.
 * - **Separators sit OUTSIDE the interactive element and are `aria-hidden`** —
 *   inside a link the glyph would join its accessible name, and the `ol`/`li`
 *   structure already conveys the hierarchy without them.
 */
const LINK_CLASSES =
  'tw-text-caption-metadata tw-text-ink-secondary motion-safe:tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift hover:tw-text-ink hover:tw-underline ' +
  `tw-rounded-ctl ${FOCUS_RING}`;

const Breadcrumbs = React.forwardRef<HTMLElement, BreadcrumbsProps>(function Breadcrumbs({
  items,
  'aria-label': ariaLabel = 'Breadcrumb',
  'data-testid': dataTestId,
  className = '',
  ...rest
}, ref) {
  return (
    <nav
      /* rest first — see the rule in Button.tsx. */
      {...rest}
      ref={ref}
      aria-label={ariaLabel}
      data-testid={dataTestId}
      className={className}
    >
      <ol className="tw-flex tw-flex-wrap tw-items-center tw-gap-1">
        {items.map((item, index) => {
          const isCurrent = index === items.length - 1;
          /* Truncation lives on an inner span so the ellipsis never eats the
             underline or the focus ring; the full text rides `title`. */
          const label = (
            <span className="tw-inline-block tw-max-w-[200px] tw-truncate tw-align-bottom" title={item.label}>
              {item.label}
            </span>
          );
          return (
            // Labels can legitimately repeat across levels; position disambiguates.
            // eslint-disable-next-line react/no-array-index-key
            <li key={`${item.label}-${index}`} className="tw-flex tw-items-center tw-gap-1 tw-min-w-0">
              {isCurrent ? (
                <span
                  aria-current="page"
                  /* Medium weight, not ink alone (§ 60): `ink` vs `ink-secondary`
                     is 1.97:1 in light but only 1.26:1 on the dark ladder — the
                     shade step vanishes there, so the weight carries it. */
                  className="tw-text-caption-metadata tw-text-ink tw-font-medium"
                  data-testid={item['data-testid']}
                >
                  {label}
                </span>
              ) : item.href ? (
                <a
                  href={item.href}
                  onClick={item.onClick}
                  className={LINK_CLASSES}
                  data-testid={item['data-testid']}
                >
                  {label}
                </a>
              ) : item.onClick ? (
                <button
                  type="button"
                  onClick={item.onClick}
                  className={LINK_CLASSES}
                  data-testid={item['data-testid']}
                >
                  {label}
                </button>
              ) : (
                /* Neither href nor onClick (§ 69): a non-navigable waypoint — the
                   delta list's category TYPE has no page of its own. A span, not a
                   button: a focusable no-op is exactly what this component's
                   docblock forbids. */
                <span
                  className="tw-text-caption-metadata tw-text-ink-secondary"
                  data-testid={item['data-testid']}
                >
                  {label}
                </span>
              )}
              {!isCurrent && (
                <ChevronRight size={12} aria-hidden="true" className="tw-shrink-0 tw-text-ink-disabled" />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
});

export default Breadcrumbs;
